import React, { type JSX, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { List, ListItem, Stack, Typography } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { usePage, useRemember } from '@inertiajs/react';
import Alert, { AlertColor } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import axios from 'axios';

import { User } from '@/types';
import { createConnection } from '@/core/echo';
import { TFlashMessage } from '@/core/ui/FlashMessage';

type FailedTaxpayer = {
  name: string;
  pid: string;
};

type MessageType = {
  message: string;
  status: AlertColor;
  failedTaxpayers?: FailedTaxpayer[];
  batchId?: string;
};

type SnackbarData = {
  open: boolean;
  message?: string;
  status?: AlertColor;
  failedTaxpayers?: FailedTaxpayer[];
  batchId?: string;
  event: string;
};

type PageProps = {
  auth: { user: User };
  flash: TFlashMessage;
  pusher: {
    appKey: string;
    cluster: string;
  };
};

enum PusherEvents {
  BULK_CREATE_ENGAGEMENT_STATUS = '.bulk-create-engagement-status',
  BULK_UPDATE_ENGAGEMENT_STATUS = '.bulk-update-engagement-status',
  BULK_CREATE_WORKFLOW_STATUS = '.bulk-create-workflow-status',
  BLUEPRINT_PROPAGATION_STATUS = '.blueprint-propagation-status',
}

const routeBatchEventsMap: Record<string, string> = {
  [PusherEvents.BULK_CREATE_ENGAGEMENT_STATUS]:
    'engagements.retryBatchCreateEngagement',
  [PusherEvents.BULK_UPDATE_ENGAGEMENT_STATUS]:
    'engagements.retryBatchUpdateEngagement',
};

const routeBatchEventsMapV2: Record<string, string> = {
  [PusherEvents.BULK_CREATE_ENGAGEMENT_STATUS]:
    'v2.engagements.retryBatchCreateEngagement',
  [PusherEvents.BULK_UPDATE_ENGAGEMENT_STATUS]:
    'v2.engagements.retryBatchUpdateEngagement',
};

export default function GlobalEngagementsStatus(): JSX.Element {
  const isV2 = window.location.pathname.includes('v2');
  const {
    props: {
      auth: { user },
      pusher,
    },
  } = usePage<PageProps>();
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useRemember<SnackbarData>(
    {
      open: false,
      message: 'undefined',
      status: 'success',
      failedTaxpayers: [],
      batchId: 'undefined',
      event: 'undefined',
    } as SnackbarData,
    `layouts/GlobalEngagementsStatus:${user.pid}`,
  );
  const echo = createConnection(pusher);

  const handleClickOpen = (): void => {
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpen(false);
  };

  echo.listen(
    `users.${user.pid}`,
    PusherEvents.BULK_CREATE_ENGAGEMENT_STATUS,
    (message: MessageType) => {
      setSnackbar({
        ...message,
        event: PusherEvents.BULK_CREATE_ENGAGEMENT_STATUS,
        open: true,
      });
    },
  );

  echo.listen(
    `users.${user.pid}`,
    PusherEvents.BULK_UPDATE_ENGAGEMENT_STATUS,
    (message: MessageType) => {
      setSnackbar({
        ...message,
        event: PusherEvents.BULK_UPDATE_ENGAGEMENT_STATUS,
        open: true,
      });
    },
  );

  echo.listen(
    `users.${user.pid}`,
    PusherEvents.BULK_CREATE_WORKFLOW_STATUS,
    (message: MessageType) => {
      setSnackbar({
        ...message,
        event: PusherEvents.BULK_CREATE_WORKFLOW_STATUS,
        open: true,
      });
    },
  );

  echo.listen(
    `users.${user.pid}`,
    PusherEvents.BLUEPRINT_PROPAGATION_STATUS,
    (message: MessageType) => {
      setSnackbar({
        ...message,
        event: PusherEvents.BLUEPRINT_PROPAGATION_STATUS,
        open: true,
      });
    },
  );

  const handleClose = (): void => {
    setSnackbar((prev) => ({
      ...prev,
      status: undefined,
      message: undefined,
      open: false,
    }));
  };

  const handleTryAgain = (): void => {
    const routeBatchEvents = isV2 ? routeBatchEventsMapV2 : routeBatchEventsMap;

    void axios.post(route(routeBatchEvents[snackbar.event]), {
      batchId: snackbar.batchId,
    });
    handleCloseDialog();
    handleClose();
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={snackbar.open}
        onClose={handleClose}
        slotProps={{
          clickAwayListener: { onClickAway: () => null },
        }}
      >
        <Alert
          elevation={8}
          onClose={handleClose}
          severity={snackbar.status}
          sx={{ width: '100%' }}
        >
          <Stack
            direction="row"
            sx={{
              maxWidth: 400,
              justifyContent: 'space-evenly',
              alignItems: 'flex-start',
              fontWeight: 500,
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>{snackbar.message}</Typography>
            {snackbar.status === 'error' && (
              <Button
                sx={{ minWidth: 150, color: 'inherit', fontWeight: 500 }}
                onClick={handleClickOpen}
              >
                Learn More
              </Button>
            )}
          </Stack>
        </Alert>
      </Snackbar>
      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle
          sx={{
            fontSize: '14px',
            alignItems: 'center',
            display: 'flex',
            gap: 2,
            color: '#CD5437',
          }}
        >
          <ErrorOutlineOutlinedIcon />
          <Typography
            sx={{
              fontSize: 18,
            }}
          >
            {snackbar.event === PusherEvents.BULK_CREATE_WORKFLOW_STATUS
              ? `${snackbar.failedTaxpayers?.length} workflows could not be created.`
              : snackbar.event === PusherEvents.BULK_CREATE_ENGAGEMENT_STATUS
                ? `${snackbar.failedTaxpayers?.length} engagements could not be created.`
                : `${snackbar.failedTaxpayers?.length} engagements could not be updated.`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ minWidth: '440px' }}>
          <DialogContentText>
            {snackbar.event === PusherEvents.BULK_CREATE_WORKFLOW_STATUS
              ? 'Creating workflows failed for:'
              : snackbar.event === PusherEvents.BULK_CREATE_ENGAGEMENT_STATUS
                ? 'Creating engagements failed for:'
                : 'Updating engagements failed for:'}
          </DialogContentText>
          <List
            sx={{
              listStyleType: 'disc',
              marginLeft: 3,
              color: '#626262',
              fontSize: '14px',
            }}
          >
            {snackbar.failedTaxpayers?.map(
              (taxpayer: FailedTaxpayer, index) => {
                return (
                  <ListItem
                    sx={{ display: 'list-item', padding: 0, color: '#626262' }}
                    key={taxpayer.pid + '-' + index}
                  >
                    {taxpayer.name}
                  </ListItem>
                );
              },
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-evenly', gap: 1 }}>
          <Button
            size="medium"
            fullWidth
            variant="outlined"
            onClick={handleCloseDialog}
          >
            Close
          </Button>
          {snackbar.batchId && (
            <Button
              size="medium"
              fullWidth
              variant="contained"
              onClick={handleTryAgain}
            >
              Try again
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
